<template>
  <dara-single-content-layout container-size="lg">
    <template v-if="question && answers" #content>
      <dara-row class="d-flex align-items-center">
        <dara-col>
          <dara-header>
            {{ question.title }}
          </dara-header>
        </dara-col>
      </dara-row>
      <dara-row class="mt-1">
        <dara-col>
          <dara-p class="dara-u-text--medium">
            {{ question.content }}
          </dara-p>

          <small class="text-muted dara-u-text--small d-block mt-4">
            <strong>{{ $t('created-by') }}:</strong>
            <span v-if="question.anonymous" class="color--danger">
              ( {{ $t('anonymous-question') }} )
            </span>
            <dara-user-link v-else :id="question.user.id" class="color--primary">
              {{ question.user.name }}
            </dara-user-link>
          </small>
          <small class="text-muted dara-u-text--small d-block">
            <strong>{{ $t('department') }}:</strong> {{ department }}
          </small>
          <small class="text-muted dara-u-text--small d-block mb-2">
            <strong>{{ $t('access') }}:</strong> {{ question.access === 0 ? $t('public') : $t('department-only') }}
          </small>

          <small class="d-flex align-items--center text-muted dara-u-text--small d-block">
            {{ $t('total-answers') }}: {{ answers.length }}
          </small>
          <div>
            <dara-tag
              v-for="tag in question.tags"
              :key="tag.id"
              :label="tag.name"
              :to="getTagLink(tag)"
              size="sm"
              type="info"
              class="mr--md mt-3 mb-1" />
          </div>
          <dara-button
            v-if="canAnswer()"
            class="color--primary mt-4 mb-3 me-2"
            @click="createAnswer()">
            {{ $t('answer') }}
          </dara-button>
          <dara-button
            v-if="canMarkAsAnswered()"
            class="color--success mt-4 mb-3 me-2"
            @click="markAsAnswered()">
            {{ $t('mark-as-answered') }}
          </dara-button>
          <dara-button
            v-if="canDelete()"
            class="color--danger mt-4 mb-3 me-2"
            @click="setDeleteQuestion(question)">
            {{ $t('delete') }}
          </dara-button>
          <dara-button v-if="canEdit">
            <dara-question-link
              :id="question.id"
              action="edit">
              {{ $t('edit') }}
            </dara-question-link>
          </dara-button>
          <br>
          <dara-invisible>
            <dara-header size="md">
              {{ $t('answers') }}
              <el-tooltip effect="dark" :content="$t('some-articles-may-not-appear-due-to-permission')" placement="right-end">
                <dara-icon size="md" icon="info-circle-fill" class="ms-1" />
              </el-tooltip>
            </dara-header>
            <div
              v-for="answer in answers"
              :key="answer.id"
              class="mb--xl">
              <dara-article-card :article="answer" />
            </div>

            <dara-p v-if="answers.length < 1" class="opacity--50 my--md">( {{ $t('no-answers') }} )</dara-p>

            <div class="d-flex justify-content-center mt-3">
              <dara-pagination
                :page.sync="pagination.page"
                :total="pagination.total"
                :page-size="pagination.limit"
                @current-change="fetchAnswers" />
            </div>
          </dara-invisible>
        </dara-col>
      </dara-row>

      <dara-modal-delete
        v-if="showModal(question)"
        :content="$t('are-you-sure-you-want-to-delete-this-question')"
        @close="setDeleteQuestion(null)"
        @confirm="onDeleteQuestion(question)">
      </dara-modal-delete>
    </template>
  </dara-single-content-layout>
</template>

<script>
import { getters } from '@/modules/user/user.store';
import { toastError } from '@/services/toastService';
import { markAsAnsweredQuestion } from '@/modules/question/question.resource';
import { goToAnswerWithArticle } from '@/modules/article/article.routes';
import { goToBasePage } from '@/router/route.service';
import { getQuestion, getAnswers, removeQuestion } from '@/modules/question/question.service';
import { getTagLink } from '@/modules/tag/tag.service';
import { translate } from '@/services/translate.service';
import { getDepartmentById } from '@/modules/department/department.service';
import { goToDeniedQuestion, goToQuestionNotFound } from '@/modules/question/question.routes';
import { QUESTION_ERRORS } from '@/modules/question/question.constants';

import DaraUserLink from '@/modules/user/components/UserLink.vue';
import DaraArticleCard from '@/modules/article/components/ArticleCard.vue';
import DaraSingleContentLayout from '@/layouts/SingleContentLayout.vue';

export default {
  name: 'QuestionViewPage',
  components: {
    DaraUserLink,
    DaraArticleCard,
    DaraSingleContentLayout,
  },
  data() {
    return {
      question: null,
      answers: null,
      department: null,
      user: getters.getUser(),
      pagination: {
        page: 1,
        limit: 5,
      },
      questionToDelete: null,
    };
  },
  computed: {
    id() {
      return this.$route.params?.id;
    },
    canEdit() {
      return this.question.user.id === this.user.id;
    },
  },
  mounted() {
    this.fetch();
  },
  methods: {
    getTagLink,
    fetch() {
      getQuestion(this.id)
        .then(({ data }) => {
          this.question = data;
          this.getDepartment(this.question.department_id);
          this.fetchAnswers();
        })
        .catch(err => {
          this.question = [];
          if (err.response.data.errors === QUESTION_ERRORS[err.response.status] && err.response.status === 403) {
            goToDeniedQuestion(this.$router);
          } else {
            goToQuestionNotFound(this.$router);
          }
        });
    },
    fetchAnswers() {
      getAnswers(this.id, this.pagination)
        .then(({ data }) => {
          this.answers = data.articles;
          Object.assign(this.pagination, data.pagination);
        })
        .catch(() => toastError(`${translate('error-on-fetching-answers')}`));
    },
    getDepartment(department) {
      getDepartmentById(department)
        .then(data => {
          this.department = data.name;
        }).catch(() => {
          toastError(`${translate('error-getting-questions-department')}`);
        });
    },
    markAsAnswered() {
      if (this.question.total_answers > 0) {
        this.question.answered = true;
        markAsAnsweredQuestion(this.question)
          .then()
          .catch(() => {
            this.question.answered = false;
            toastError(`${translate('error-marking-question-as-answered')}`);
          });
      } else {
        toastError(`${translate('error-marking-question-as-answered')}`);
      }
    },
    createAnswer() {
      goToAnswerWithArticle(this.$router, this.question);
    },
    canAnswer() {
      return !this.question.answered
        && this.user.permission > 0;
    },
    canMarkAsAnswered() {
      return !this.question.answered
        && this.answers.length > 0
        && this.user.id === this.question.user_id;
    },
    canDelete() {
      return this.question.user_id === this.user.id || this.user.permission > 1;
    },
    setDeleteQuestion(question) {
      this.questionToDelete = question;
    },
    showModal(question) {
      return this.questionToDelete && this.questionToDelete.id === question.id;
    },
    onDeleteQuestion(question) {
      removeQuestion(question).then(() => goToBasePage());
    },
  },
};
</script>
